import React, { useEffect, useMemo, useState } from "react";
import {
    IonAccordion,
    IonAccordionGroup,
    IonAlert,
    IonBadge,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonText,
    IonTitle,
    IonToolbar,
} from "@ionic/react";
import type { Mapping, Provider } from "../AcaSettingsTypes";

interface ModalManageProviderMappingsProps {
    modal: {
        isOpen: boolean;
        onDidDismiss: () => void;
    };
    data: {
        pendingMappingState: string | null;
        providers: Provider[];
        mappings: Mapping[];
    };
    actions: {
        onRemoveMapping: (state: string, providerId: number) => Promise<boolean> | boolean;
        onAddMapping: (state: string, providerId: number) => Promise<boolean> | boolean;
    };
}

const ModalManageProviderMappings: React.FC<ModalManageProviderMappingsProps> = ({modal, data, actions,}) => {
    const { isOpen, onDidDismiss } = modal;
    const { pendingMappingState, providers, mappings } = data;
    const { onRemoveMapping, onAddMapping } = actions;

    const [pendingMappingProviderId, setPendingMappingProviderId] = useState<number | null>(null);
    const [mappingConfirmOpen, setMappingConfirmOpen] = useState(false);
    const [pendingRemoveProviderId, setPendingRemoveProviderId] = useState<number | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setPendingMappingProviderId(null);
            setMappingConfirmOpen(false);
            setPendingRemoveProviderId(null);
            return;
        }

        setPendingMappingProviderId(null);
        setMappingConfirmOpen(false);
        setPendingRemoveProviderId(null);
    }, [isOpen, pendingMappingState]);

    const activeProvidersForMapping = useMemo(
        () =>
            (providers || [])
                .filter((provider) => provider.status === "ACTIVE")
                .sort((a, b) => a.name.localeCompare(b.name)),
        [providers]
    );

    const mapped = useMemo(
        () =>
            !pendingMappingState
                ? []
                : (mappings || [])
                    .filter((mapping) => mapping.state === pendingMappingState)
                    .map((mapping) => ({ name: mapping.name, providerId: mapping.providerId }))
                    .sort((a, b) => a.name.localeCompare(b.name)),
        [mappings, pendingMappingState]
    );

    const mappedIds = mapped.map((mapping) => mapping.providerId);
    const available = activeProvidersForMapping.filter((provider) => !mappedIds.includes(provider.id));

    const getProviderName = (providerId: number): string => {
        return providers.find((provider) => provider.id === providerId)?.name || `Provider ${providerId}`;
    };

    const removeWarning = useMemo(() => {
        if (!pendingMappingState || pendingRemoveProviderId == null) {
            return "";
        }

        const currentCount = mappings.filter((mapping) => mapping.state === pendingMappingState).length;
        return currentCount === 1
            ? `\n\nWarning: ${pendingMappingState} will no longer appear as a disposition option until a provider is added.`
            : "";
    }, [mappings, pendingMappingState, pendingRemoveProviderId]);

    const handleConfirmAddMapping = async () => {
        if (!pendingMappingState || !pendingMappingProviderId) {
            setMappingConfirmOpen(false);
            return;
        }

        const success = await onAddMapping(pendingMappingState, pendingMappingProviderId);
        if (success) {
            setPendingMappingProviderId(null);
            setMappingConfirmOpen(false);
        }
    };

    const handleConfirmRemoveMapping = async () => {
        if (!pendingMappingState || pendingRemoveProviderId == null) {
            setPendingRemoveProviderId(null);
            return;
        }

        const success = await onRemoveMapping(pendingMappingState, pendingRemoveProviderId);
        if (success) {
            setPendingRemoveProviderId(null);
        }
    };

    return (
        <>
            <IonModal isOpen={isOpen} onDidDismiss={onDidDismiss}>
                <IonHeader>
                    <IonToolbar color="primary">
                        <IonTitle>
                            {pendingMappingState
                                ? `Manage Providers for ${pendingMappingState}`
                                : "Manage Providers"}
                        </IonTitle>
                        <IonButtons slot="end">
                            <IonButton onClick={onDidDismiss}>Close</IonButton>
                        </IonButtons>
                    </IonToolbar>
                </IonHeader>

                <IonContent className="ion-padding">
                    {!pendingMappingState && (
                        <IonText color="medium">
                            <p>No state selected.</p>
                        </IonText>
                    )}

                    {pendingMappingState && (
                        <>
                            <div style={{ marginTop: 24 }}>
                                <IonText>
                                    <h2 style={{ marginTop: 0 }}>Add Provider Mapping</h2>
                                </IonText>

                                {available.length === 0 ? (
                                    <IonItem lines="none">
                                        <IonLabel color="medium">
                                            There are no additional active providers available to add for this
                                            state.
                                        </IonLabel>
                                    </IonItem>
                                ) : (
                                    <>
                                        <IonItem>
                                            <IonSelect
                                                label="Provider"
                                                labelPlacement="floating"
                                                interface="popover"
                                                value={pendingMappingProviderId ?? undefined}
                                                placeholder="Select provider"
                                                onIonChange={(e) =>
                                                    setPendingMappingProviderId(Number(e.detail.value))
                                                }
                                            >
                                                {available.map((provider) => (
                                                    <IonSelectOption key={provider.id} value={provider.id}>
                                                        {provider.name}
                                                    </IonSelectOption>
                                                ))}
                                            </IonSelect>
                                        </IonItem>

                                        <div
                                            style={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                marginTop: 12,
                                            }}
                                        >
                                            <IonButton
                                                disabled={
                                                    !pendingMappingProviderId ||
                                                    !available.some(
                                                        (provider) => provider.id === pendingMappingProviderId
                                                    )
                                                }
                                                onClick={() => {
                                                    if (!pendingMappingState || !pendingMappingProviderId) {
                                                        return;
                                                    }
                                                    setMappingConfirmOpen(true);
                                                }}
                                            >
                                                Add Mapping
                                            </IonButton>
                                        </div>
                                    </>
                                )}
                            </div>

                            <IonAccordionGroup>
                                <IonAccordion value="mapped-providers">
                                    <IonItem
                                        slot="header"
                                        color="light"
                                        lines="full"
                                        style={{ marginTop: 16 }}
                                    >
                                        <IonLabel>Mapped Providers</IonLabel>
                                        <IonBadge slot="end">{mapped.length}</IonBadge>
                                    </IonItem>

                                    <div slot="content" style={{ padding: "8px 0 0" }}>
                                        {mapped.length === 0 ? (
                                            <IonItem lines="none">
                                                <IonLabel color="medium">
                                                    No providers are currently mapped to this state.
                                                </IonLabel>
                                            </IonItem>
                                        ) : (
                                            <IonList>
                                                {mapped.map((mapping) => (
                                                    <IonItem key={mapping.providerId}>
                                                        <IonLabel>{mapping.name}</IonLabel>
                                                        <IonButton
                                                            slot="end"
                                                            color="danger"
                                                            size="small"
                                                            fill="outline"
                                                            onClick={() => {
                                                                setPendingRemoveProviderId(mapping.providerId);
                                                            }}
                                                        >
                                                            Remove
                                                        </IonButton>
                                                    </IonItem>
                                                ))}
                                            </IonList>
                                        )}
                                    </div>
                                </IonAccordion>
                            </IonAccordionGroup>
                        </>
                    )}
                </IonContent>
            </IonModal>

            <IonAlert
                isOpen={mappingConfirmOpen}
                header="Add mapping?"
                onDidDismiss={() => {
                    setMappingConfirmOpen(false);
                }}
                message={
                    pendingMappingState && pendingMappingProviderId
                        ? `Add “${getProviderName(pendingMappingProviderId)}” to state ${pendingMappingState}?`
                        : "Add provider mapping?"
                }
                buttons={[
                    {
                        text: "Back",
                        role: "cancel",
                        handler: () => {
                            setMappingConfirmOpen(false);
                        },
                    },
                    {
                        text: "Add",
                        role: "confirm",
                        handler: () => {
                            void handleConfirmAddMapping();
                            return false;
                        },
                    },
                ]}
            />

            <IonAlert
                isOpen={pendingRemoveProviderId != null}
                header="Remove mapping?"
                onDidDismiss={() => {
                    setPendingRemoveProviderId(null);
                }}
                message={
                    pendingMappingState && pendingRemoveProviderId != null
                        ? `Remove “${getProviderName(pendingRemoveProviderId)}” from state ${pendingMappingState}?${removeWarning}`
                        : "Remove provider mapping?"
                }
                buttons={[
                    {
                        text: "Cancel",
                        role: "cancel",
                        handler: () => {
                            setPendingRemoveProviderId(null);
                        },
                    },
                    {
                        text: "Remove",
                        role: "destructive",
                        handler: () => {
                            void handleConfirmRemoveMapping();
                            return false;
                        },
                    },
                ]}
            />
        </>
    );
};

export default ModalManageProviderMappings;